// Message builders and constants for openSnack calls.

import snackTerms from "~/helpers/snackTerms";
import i18n from "~/i18n/i18n";

export type ActionSuccessKey =
	| "eventStatusUpdate"
	| "growPointTriggerWorker"
	| "growPointValidatePrices"
	| "itemCombineReverted"
	| "itemsCombined"
	| "portalAddressSwitched"
	| "productionCatalogsCombined"
	| "productionItemsCombined"
	| "scheduleConsolidation"
	| "siteLocationCombine"
	| "siteLocationRevertCombine"
	| "termsCombine"
	| "termsRevertCombine";

export interface LinkSummaryCounts {
	linked: number;
	notReached: number;
	rejected: number;
	total: number;
}

const t = (key: string, values?: Record<string, unknown>): string =>
	i18n.t(`snack.${key}`, { ns: "common", ...values });

const withEntity = (key: string, entity: string, values?: Record<string, unknown>): string => {
	const term = snackTerms.entityTerm(entity);
	return t(key, { context: term.grammar, entity: term.text, ...values });
};

const linkSummaryDetail = (counts: LinkSummaryCounts, refusedKey: string): string => {
	const refused = counts.rejected ? t(refusedKey, { count: counts.rejected }) : "";
	const notReached = counts.notReached ? t("productionItemNotReached", { count: counts.notReached }) : "";
	if (refused && notReached) return t("productionItemSummaryBoth", { notReached, refused });
	return refused || notReached;
};

const snackMessages = {
	actionError: (action: string, detail?: string) =>
		detail
			? t("actionErrorWithDetail", { action: snackTerms.actionText(action), detail })
			: t("actionError", { action: snackTerms.actionText(action) }),
	actionSuccess: (action: ActionSuccessKey) => t(`actionSuccess.${action}`),
	activateError: (entity: string) => t("activateError", { entity: snackTerms.entityTerm(entity).text }),
	activateSuccess: (entity: string) => withEntity("activateSuccess", entity),
	addError: (entity: string) => t("addError", { entity: snackTerms.entityTerm(entity).text }),
	addressNotFound: (entity: string) => t("addressNotFound", { entity: snackTerms.entityTerm(entity).text }),
	addSuccess: (entity: string) => withEntity("addSuccess", entity),
	barcodeCapturedUnmatched: (code: string) => t("barcodeCapturedUnmatched", { code }),
	barcodeCapturedUnverified: (code: string) => t("barcodeCapturedUnverified", { code }),
	batchSavePartial: (ok: number, failed: number, detail?: string) =>
		detail
			? t("batchSavePartialWithDetail", { count: failed, detail, ok })
			: t("batchSavePartial", { count: failed, ok }),
	batchSaveSuccess: (count: number) => t("batchSaveSuccess", { count }),
	calculateError: (entity: string) => t("calculateError", { entity: snackTerms.entityTerm(entity).text }),
	calculateSuccess: (entity: string) => withEntity("calculateSuccess", entity),
	get changesDiscarded() {
		return t("changesDiscarded");
	},
	createError: (entity: string) => t("createError", { entity: snackTerms.entityTerm(entity).text }),
	createSuccess: (entity: string) => withEntity("createSuccess", entity),
	defaultChanged: (entity: string, newName: string, previousName?: string) =>
		previousName
			? withEntity("defaultChangedFrom", entity, { newName, previousName })
			: withEntity("defaultChangedTo", entity, { newName }),
	deleteError: (entity: string) => t("deleteError", { entity: snackTerms.entityTerm(entity).text }),
	deleteSuccess: (entity: string) => withEntity("deleteSuccess", entity),
	get editModeDisabled() {
		return t("editModeDisabled");
	},
	get editModeEnabled() {
		return t("editModeEnabled");
	},
	failedOfTotal: (failed: number, total: number, entity: string, detail: string) =>
		t("failedOfTotal", { count: total, detail, entity: snackTerms.countedEntityText(entity, total), failed }),
	get fernieSuggestionsSaveError() {
		return t("fernieSuggestionsSaveError");
	},
	get gridDataRefreshError() {
		return t("gridDataRefreshError");
	},
	get gridRefreshError() {
		return t("gridRefreshError");
	},
	get gridUnavailable() {
		return t("gridUnavailable");
	},
	hiddenInvalidId: (count: number, entity: string) =>
		t("hiddenInvalidId", { count, entity: snackTerms.countedEntityText(entity, count) }),
	inactivateError: (entity: string) => t("inactivateError", { entity: snackTerms.entityTerm(entity).text }),
	inactivateSuccess: (entity: string) => withEntity("inactivateSuccess", entity),
	itemNotFound: (code: string) => t("itemNotFound", { code }),
	get itemsReceived() {
		return t("itemsReceived");
	},
	get laborTypeCreatedRateFailed() {
		return t("laborTypeCreatedRateFailed");
	},
	get laborTypeSiteLocationRequired() {
		return t("laborTypeSiteLocationRequired");
	},
	get localPreferenceSaveError() {
		return t("localPreferenceSaveError");
	},
	linkError: (entity: string) => t("linkError", { entity: snackTerms.entityTerm(entity).text }),
	linkSuccess: (count: number, entity: string) =>
		t("linkSuccess", { count, entity: snackTerms.countedEntityText(entity, count) }),
	listStillLoading: (entity: string) => t("listStillLoading", { entity: snackTerms.entityTerm(entity).text }),
	loadError: (entity: string) => t("loadError", { entity: snackTerms.entityTerm(entity).text }),
	manualCleanupError: (entity: string) => t("manualCleanupError", { entity: snackTerms.entityTerm(entity).text }),
	get noChangesToSave() {
		return t("noChangesToSave");
	},
	partialCreateAttributesError: (entity: string) =>
		t("partialCreateAttributesError", { entity: snackTerms.entityTerm(entity).text }),
	partialCreateOrderEntryUnitError: (entity: string) =>
		t("partialCreateOrderEntryUnitError", { entity: snackTerms.entityTerm(entity).text }),
	partialCreatePriceEntryUnitError: (entity: string) =>
		t("partialCreatePriceEntryUnitError", { entity: snackTerms.entityTerm(entity).text }),
	partialError: (entity: string, count: number) =>
		t("partialError", { count, entity: snackTerms.entityTerm(entity).text }),
	partialUpdateAttributesError: (entity: string) =>
		t("partialUpdateAttributesError", { entity: snackTerms.entityTerm(entity).text }),
	partialUpdateLoadLinesError: (entity: string) =>
		t("partialUpdateLoadLinesError", { entity: snackTerms.entityTerm(entity).text }),
	partialUpdateShippingUomsError: (entity: string) =>
		t("partialUpdateShippingUomsError", { entity: snackTerms.entityTerm(entity).text }),
	partialSuccess: (ok: number, total: number, noun: string) =>
		withEntity("partialSuccess", noun, { failed: total - ok, noun: snackTerms.entityTerm(noun).text, ok, total }),
	pollingFailingPersistent: (entity: string) =>
		t("pollingFailingPersistent", { entity: snackTerms.entityTerm(entity).text }),
	pollingHaltedError: (entity: string) => t("pollingHaltedError", { entity: snackTerms.entityTerm(entity).text }),
	pollingRetryWarning: (entity: string) => t("pollingRetryWarning", { entity: snackTerms.entityTerm(entity).text }),
	productionItemLinkSummary: (counts: LinkSummaryCounts) =>
		t("productionItemLinkSummary", { ...counts, detail: linkSummaryDetail(counts, "productionItemLinkRefused") }),
	productionItemUnlinkSummary: (counts: LinkSummaryCounts) =>
		t("productionItemUnlinkSummary", { ...counts, detail: linkSummaryDetail(counts, "productionItemUnlinkRefused") }),
	get purchaseOrderReceived() {
		return t("purchaseOrderReceived");
	},
	get rateCancelCurrent() {
		return t("rateCancelCurrent");
	},
	get rateCancelHistorical() {
		return t("rateCancelHistorical");
	},
	get rateEditNoop() {
		return t("rateEditNoop");
	},
	get rateHistoryVerifyFailed() {
		return t("rateHistoryVerifyFailed");
	},
	get rateNotFound() {
		return t("rateNotFound");
	},
	get rateSiteTimezoneRequired() {
		return t("rateSiteTimezoneRequired");
	},
	refreshEntityError: (entity: string) => t("refreshEntityError", { entity: snackTerms.entityTerm(entity).text }),
	removeError: (entity: string) => t("removeError", { entity: snackTerms.entityTerm(entity).text }),
	removeSuccess: (entity: string) => withEntity("removeSuccess", entity),
	retrySyncFailed: (detail?: string) => (detail ? t("retrySyncFailedWithDetail", { detail }) : t("retrySyncFailed")),
	retrySyncPartial: (requeued: number, total: number, failed: number, detail?: string) =>
		detail
			? t("retrySyncPartialWithDetail", { detail, failed, requeued, total })
			: t("retrySyncPartial", { failed, requeued, total }),
	retrySyncQueued: (count: number) => t("retrySyncQueued", { count }),
	retrying: (entity: string) => withEntity("retrying", entity),
	saveError: (entity: string) => t("saveError", { entity: snackTerms.entityTerm(entity).text }),
	savedQueued: (entity: string) => withEntity("savedQueued", entity),
	saveSuccess: (entity: string) => withEntity("saveSuccess", entity),
	selectAtLeastOne: (entity: string) => withEntity("selectAtLeastOne", entity),
	syncFailed: (count: number, detail?: string) =>
		detail ? t("syncFailedWithDetail", { count, detail }) : t("syncFailed", { count }),
	get syncOffline() {
		return t("syncOffline");
	},
	syncPending: (count: number) => t("syncPending", { count }),
	synced: (entity: string) => withEntity("synced", entity),
	syncing: (count: number) => t("syncing", { count }),
	undeleteError: (entity: string) => t("undeleteError", { entity: snackTerms.entityTerm(entity).text }),
	undeleteSuccess: (entity: string) => withEntity("undeleteSuccess", entity),
	unlinkError: (entity: string) => t("unlinkError", { entity: snackTerms.entityTerm(entity).text }),
	unlinkSuccess: (count: number, entity: string) =>
		t("unlinkSuccess", { count, entity: snackTerms.countedEntityText(entity, count) }),
	updatedAndActivated: (entity: string) => withEntity("updatedAndActivated", entity),
	updateError: (entity: string) => t("updateError", { entity: snackTerms.entityTerm(entity).text }),
	updateSuccess: (entity: string) => withEntity("updateSuccess", entity),
	voidSuccess: (entity: string) => withEntity("voidSuccess", entity),
	updateUnconfirmed: (entity: string) => t("updateUnconfirmed", { entity: snackTerms.entityTerm(entity).text }),
	validationRequired: (field: string) =>
		withEntity("validationRequired", field, { field: snackTerms.entityTerm(field).text })
} as const;

export default snackMessages;
